import { extractAptUnit } from './addressParser'

export type GooglePlacePhysicalAddressFields = {
  address1: string
  address2: string
  city: string
  state: string
  zipcode: string
}

type PlaceLike = {
  address_components?: Array<{
    long_name: string
    short_name: string
    types: string[]
  }>
  formatted_address?: string
}

/** True when all required physical-address fields are non-empty. */
export function hasCompletePhysicalAddressFields(fields: {
  address1?: string | null
  city?: string | null
  state?: string | null
  zipcode?: string | null
}): boolean {
  return Boolean(
    String(fields.address1 ?? '').trim() &&
      String(fields.city ?? '').trim() &&
      String(fields.state ?? '').trim() &&
      String(fields.zipcode ?? '').trim(),
  )
}

/**
 * Maps Google Places `address_components` into physical address form fields.
 * Missing street/city/state/zip become empty strings so Set Address can wipe prior
 * values (Angular `setGoogleAddress` clear-then-apply).
 * Address Line 2 is cleared unless Google provides `subpremise`, or an apt/suite
 * can be parsed from `formatted_address`.
 */
export function mapGooglePlaceToPhysicalAddressFields(
  place: PlaceLike,
): GooglePlacePhysicalAddressFields | null {
  if (!place.address_components?.length) return null

  let streetNumber = ''
  let route = ''
  let city = ''
  let cityFallback = ''
  let state = ''
  let zipcode = ''
  let address2 = ''

  for (const comp of place.address_components) {
    const type = comp.types[0]
    if (!type) continue

    if (type === 'street_number') streetNumber = comp.long_name
    if (type === 'route') route = comp.long_name
    if (type === 'subpremise') address2 = comp.long_name

    if (
      type === 'locality' ||
      type === 'postal_town' ||
      type === 'neighborhood' ||
      type === 'sublocality_level_1'
    ) {
      city = comp.long_name
    }
    if (
      !cityFallback &&
      (type === 'administrative_area_level_2' || type === 'administrative_area_level_3')
    ) {
      cityFallback = comp.long_name
    }
    if (type === 'administrative_area_level_1') state = comp.short_name
    if (type === 'postal_code') zipcode = comp.long_name
  }

  if (!address2 && place.formatted_address) {
    const suiteMatch = place.formatted_address.match(
      /(?:apt\.?|apartment|unit|suite|ste\.?|#)\s*[a-z0-9-]+/i,
    )
    if (suiteMatch?.[0]) {
      address2 = suiteMatch[0].trim()
    } else {
      const apt = extractAptUnit(place.formatted_address)
      if (apt) address2 = apt
    }
  }

  return {
    address1: streetNumber ? `${streetNumber} ${route}`.trim() : route,
    address2,
    city: city || cityFallback,
    state,
    zipcode,
  }
}
